import { Vector3 } from 'three'
import { BUS_SKID } from '../game/tuning'
import { curveAt } from './busParticles'

/**
 * 📐 `SkidEffect/Skid` — a Unity `TrailRenderer`, as a ribbon of samples.
 *
 * The two rear wheels each drag one. `Bus.ToggleSkidEmitting` switches them,
 * and `BUS_SKID` holds the prefab's numbers — see there for the trap about
 * `DepartAndExit`'s call being a no-op.
 *
 * A `TrailRenderer` is not a particle system and does not behave like one:
 *
 *  - It records a **point** each time the emitter has moved `minVertexDistance`
 *    since the last one, not on a clock. A stationary wheel adds nothing.
 *  - Every point ages out after `m_Time` seconds regardless of emission, so a
 *    trail behind a stopped bus shortens from the tail and vanishes.
 *  - Width and colour are read along the ribbon **by position between head and
 *    tail**, not by each point's own age. The head is always full width.
 *
 * ⚠️ The last one is the difference that shows. Fading each point by its own age
 * gives a ribbon whose middle can be more opaque than its head; Unity's is a
 * clean taper from the wheel backwards, every frame.
 */

export interface SkidPoint {
  readonly position: Vector3
  /** Seconds since it was recorded. Dropped past `BUS_SKID.seconds`. */
  age: number
}

/** One wheel's ribbon. */
export interface SkidRibbon {
  points: SkidPoint[]
  /** Whether `ToggleSkidEmitting` currently has it on. */
  emitting: boolean
}

export const newRibbon = (): SkidRibbon => ({ points: [], emitting: false })

/**
 * Age the ribbon, drop what has expired, and record a new head if the wheel has
 * moved far enough.
 *
 * `at` is where the wheel is **now**, in world space.
 */
export function advanceSkid(ribbon: SkidRibbon, at: Vector3, dt: number): SkidRibbon {
  if (dt > 0) {
    let write = 0
    for (const point of ribbon.points) {
      point.age += dt
      if (point.age >= BUS_SKID.seconds) continue
      ribbon.points[write++] = point
    }
    ribbon.points.length = write
  }

  if (!ribbon.emitting) return ribbon

  // `m_MinVertexDistance`: the head is only committed once the wheel has moved
  // far enough from it. Without this a parked bus grows a ribbon of hundreds of
  // coincident points, which is both a leak and a hard-edged blob.
  const head = ribbon.points[ribbon.points.length - 1]
  if (head && head.position.distanceTo(at) < BUS_SKID.minVertexDistance) return ribbon
  ribbon.points.push({ position: at.clone(), age: 0 })
  return ribbon
}

/** Stop emitting. What is already down stays until it ages out. */
export function stopSkid(ribbon: SkidRibbon): void {
  ribbon.emitting = false
}

/**
 * The ribbon as quads, head first: `[centre, halfWidth, alpha]` per point.
 *
 * Position along the ribbon is `i / (count - 1)` from head to tail, which is
 * what Unity samples `widthCurve` and `colorGradient` with — not the point's own
 * age. A one-point ribbon has no length and is skipped by the caller.
 */
export function skidSegments(
  ribbon: SkidRibbon,
): { position: Vector3; halfWidth: number; alpha: number }[] {
  const count = ribbon.points.length
  if (count < 2) return []
  const out: { position: Vector3; halfWidth: number; alpha: number }[] = []
  for (let i = 0; i < count; i++) {
    // `points` is oldest-first, so the head — the newest — is the last entry,
    // and t = 0 has to be there.
    const t = 1 - i / (count - 1)
    out.push({
      position: ribbon.points[i].position,
      halfWidth: (BUS_SKID.width * curveAt(BUS_SKID.widthCurve, t)) / 2,
      alpha: curveAt(BUS_SKID.alphaCurve, t),
    })
  }
  return out
}
